'use strict';

define('vb/private/stateManagement/vbWebSocket',['vb/private/constants', 'vb/private/helpers/rest', 'vb/private/log'],
  (Constants, RestHelper, Log) => {
    /**
     * @typedef {import('vb/private/stateManagement/container').default} Container
     */

    const logger = Log.getLogger('/vb/stateManagement/websocket');
    const RECONNECT_DELAY = 5000;
    const MAX_RECONNECT_RETRIES = 20;

    /**
     * Represents a WebSocket connection with additional features such as automatic reconnection and event handling.
     */
    class VbWebSocket {
      /**
       * Initializes a new instance of the VbWebSocket class.
       *
       * @param {string} name - The name of the WebSocket connection.
       * @param {Object} definition - An object containing configuration details for the WebSocket connection.
       * @param {string} definition.endpoint - The endpoint reference for the WebSocket
       * @param {Object} definition.beforeOpen - The listener definition for the beforeOpen event
       * @param {Object} definition.onOpen - The listener definition for the onOpen event
       * @param {Object} definition.onClose - The listener definition for the onClose event
       * @param {Object} definition.onError - The listener definition for the onError event
       * @param {Object} definition.onMessage - The listener definition for the onMessage event
       * @param {Object} definition.onDisconnect - The listener definition for the onDisconnect event
       * @param {Object} definition.onReconnect - The listener definition for the onConnect event
       * @param {Container} container - An object providing contextual information and utility functions.
       */
      constructor(name, definition, container) {
        this.name = name;
        this.definition = definition;
        this.container = container;
        this.endpoint = definition.endpoint;
        this.registeredEventListeners = [];
        this.state = 'init';
        this.webSocket = null;

        // represents a non-functional webSocket before the real one is created
        this.emptyWebSocket = null;

        this.reconnectRetryCount = 0;
      }

      /**
       * Returns a proxy object that provides access to the underlying WebSocket instance.
       * The proxy supports the following methods:
       * - `send(data)`: Sends data over the WebSocket connection. If the connection is disconnected, it will
       * automatically reconnect before sending the data.
       * - `close()`: Closes the WebSocket connection.
       *
       * Any other properties or methods accessed through the proxy will be forwarded directly to the underlying
       * WebSocket instance.
       *
       * @returns {Object|Proxy} proxy object providing controlled access to the WebSocket instance or an empty
       * object if there is no underlying webSocket
       */
      get instance() {
        return !this.webSocket ? this.emptyWebSocket : new Proxy(this.webSocket, {
          get: (target, prop) => {
            if (prop === 'send') {
              return async (data) => {
                if (this.state === 'init') {
                  await this.activatePromise;
                } else if (this.state === 'disconnected') {
                  await this.initiateReconnect();
                }
                this.webSocket.send(data);
              };
            }

            if (prop === 'close') {
              // prevent calling close if the underlying webSocket has been disposed
              return this.webSocket ? this.webSocket.close.bind(this.webSocket) : () => {};
            }

            return Reflect.get(target, prop);
          },
        });
      }

      /**
       * Establishes a WebSocket connection to the specified endpoint and sets up event listeners.
       *
       * This method creates a new WebSocket instance with the provided endpoint URL and adds event listeners for open,
       * close, message, and error events.
       *
       * @async
       * @returns {Promise<void>} Returns a Promise that resolves after the WebSocket is created and listeners
       *                          are added.
       */
      async activate() {
        try {
          // invoke the beforeOpen callback if any to set up the webSocket url which should be available in
          // this.emptyWebSocket
          await this.handleBeforeOpenEvent();

          // fallback to using endpoint reference if no url is configured by the beforeOpen callback
          const websocketURL = (this.emptyWebSocket && this.emptyWebSocket.url)
          || await RestHelper.get(this.endpoint).toUrl();

          if (!websocketURL) {
            logger.error('Failed to create WebSocket', this.name, 'due to an invalid endpoint', this.endpoint);
            return;
          }

          this.webSocket = new WebSocket(websocketURL);

          if (this.state === 'init') {
            this.activatePromise = this.activatePromise || new Promise((resolve, reject) => {
              this.activateResolve = resolve;
              this.activateReject = reject;
            })
              .finally(() => {
                this.activateResolve = null;
                this.activateReject = null;
              });
          }

          this.addEventListeners();
        } catch (error) {
          logger.error('Failed to activate WebSocket', this.name, 'with error:', error);
        }
      }

      /**
       * Handles the `beforeOpen` listener for this WebSocket definition.
       *
       * If a `beforeOpen` event is configured, this method prepares a placeholder WebSocket object
       * (`this.emptyWebSocket`) and invokes the configured listener chain(s) via {@link VbWebSocket#handleEvent}.
       *
       * The `beforeOpen` chain can use the `webSocket` context (see {@link Constants.ContextName.WEB_SOCKET}) to
       * provide a URL for the upcoming connection (for example by setting `webSocket.url`).
       *
       * This method is awaited by {@link VbWebSocket#activate} before the real `WebSocket` instance is created.
       *
       * @async
       * @returns {Promise<void>} Resolves after the `beforeOpen` listener chain(s) complete, or immediately when
       *                          no `beforeOpen` event is configured.
       */
      async handleBeforeOpenEvent() {
        const eventDef = this.definition.beforeOpen;

        if (eventDef) {
          this.webSocket = null;
          this.emptyWebSocket = {};

          await this.handleEvent(eventDef);
        }
      }

      /**
       * Initiates a reconnect attempt for the WebSocket connection.
       *
       * If a reconnect attempt is already in progress, this method returns the existing promise.
       * Otherwise, it starts a new reconnect attempt by calling {@link VbWebSocket#reconnect}.
       * Once the reconnect attempt completes (either successfully or with failure), the reconnect promise is reset.
       *
       * @async
       * @returns {Promise<void>} A promise that resolves when the reconnect attempt completes.
       */
      async initiateReconnect() {
        this.reconnectPromise = this.reconnectPromise || this.reconnect()
          .finally(() => {
            this.reconnectPromise = null;
            this.reconnectRetryCount = 0;
          });

        return this.reconnectPromise;
      }

      /**
       * Attempts to reconnect the WebSocket connection.
       *
       * If the maximum number of reconnect attempts has been reached, an error is thrown.
       * Otherwise, the reconnect retry count is incremented, and a reconnect attempt is initiated.
       *
       * @async
       * @throws {Error} If the maximum number of reconnect attempts has been reached.
       * @returns {Promise<void>} A promise that resolves when the reconnect attempt completes.
       */
      async reconnect() {
        if (this.reconnectRetryCount >= MAX_RECONNECT_RETRIES) {
          throw new Error(`Failed to reconnect WebSocket ${this.name} after ${this.reconnectRetryCount} tries.`);
        }

        this.reconnectRetryCount += 1;

        logger.info('Reconnecting WebSocket', this.name, ', attempt', this.reconnectRetryCount);

        const reconnectPromise = new Promise((resolve, reject) => {
          this.reconnectResolve = resolve;
          this.reconnectReject = reject;
          setTimeout(() => this.activate(), RECONNECT_DELAY);
        })
          .finally(() => {
            this.reconnectResolve = null;
            this.reconnectReject = null;
          });

        return reconnectPromise.catch(() => this.reconnect());
      }

      /**
       * Adds event listeners to the WebSocket instance for various events.
       *
       * This method sets up listeners for the following events:
       * - Open: Triggered when the WebSocket connection is established.
       * - Close: Triggered when the WebSocket connection is closed.
       * - Message: Triggered when a message is received over the WebSocket connection.
       * - Error: Triggered when an error occurs on the WebSocket connection.
       *
       * Each event listener is responsible for handling the corresponding event according to the definitions
       * provided in the WebSocket configuration.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      addEventListeners() {
        this.addOnOpenListener();
        this.addOnCloseListener();
        this.addOnMessageListener();
        this.addOnErrorListener();
      }

      /**
       * Adds an event listener for the 'open' event on the WebSocket connection.
       *
       * When the WebSocket connection is established, this listener checks if there's an 'onOpen' event defined in the
       * WebSocket configuration. If so, it triggers the associated event handler. Additionally, it updates the
       * WebSocket state accordingly ('init' -> 'open' or 'disconnected' -> 'reconnected'). If reconnecting, it also
       * handles the 'onReconnect' event if defined and resolves the reconnect promise.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      addOnOpenListener() {
        const listener = (event) => {
          const eventDef = this.definition.onOpen;
          if (eventDef) {
            this.handleEvent(eventDef, event);
          }

          if (this.state === 'init') {
            this.state = 'open';
            this.activateResolve();
          } else if (this.state === 'disconnected') {
            this.state = 'reconnected';
            const onReconnectEventDef = this.definition.onReconnect;
            if (onReconnectEventDef) {
              this.handleEvent(onReconnectEventDef, event);
            }

            if (this.reconnectResolve) {
              this.reconnectResolve();
            }
          }
        };

        this.webSocket.addEventListener('open', listener);
        this.registeredEventListeners.push({
          type: 'open',
          listener,
        });
      }

      /**
       * Adds an event listener for the 'close' event on the WebSocket connection.
       *
       * When the WebSocket connection is closed, this listener updates the connection state to 'disconnected',
       * triggers the 'onClose' and 'onDisconnect' events if defined in the WebSocket configuration,
       * removes all registered event listeners, and initiates a reconnect attempt if necessary.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      addOnCloseListener() {
        const listener = (event) => {
          if (this.state === 'init') {
            this.state = 'disconnected';
            this.activateReject();
          } else if (this.state === 'open' || this.state === 'reconnected') {
            this.state = 'disconnected';

            const eventDef = this.definition.onClose;
            if (eventDef) {
              this.handleEvent(eventDef, event);
            }

            const onDisconnectEventDef = this.definition.onDisconnect;
            if (onDisconnectEventDef) {
              this.handleEvent(onDisconnectEventDef, event);
            }
          }

          this.removeEventListeners();

          if (this.reconnectReject) {
            this.reconnectReject();
          } else {
            this.initiateReconnect();
          }
        };

        this.webSocket.addEventListener('close', listener);
        this.registeredEventListeners.push({
          type: 'close',
          listener,
        });
      }

      /**
       * Adds an event listener for the 'message' event on the WebSocket connection.
       *
       * If an 'onMessage' event is defined in the WebSocket configuration, this method sets up a listener function that
       * processes incoming messages. The listener function extracts the message data, parses it according to the
       * specified message type (JSON or string), and then passes the parsed message to the `handleEvent` method for
       * further processing.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      addOnMessageListener() {
        const eventDef = this.definition.onMessage;
        if (eventDef) {
          const listener = async (event) => {
            const { data } = event;
            const { messageType } = eventDef;

            let message = data;
            if (message instanceof Blob) {
              if (messageType === 'json' || messageType === 'string') {
                message = await data.text();
              }
            }
            if (typeof message === 'string' && messageType === 'json') {
              message = JSON.parse(message);
            }

            this.handleEvent(eventDef, event, message);
          };
          this.webSocket.addEventListener('message', listener);
          this.registeredEventListeners.push({
            type: 'message',
            listener,
          });
        }
      }

      /**
       * Adds an event listener for the 'error' event on the WebSocket connection.
       *
       * If an 'onError' event is defined in the WebSocket configuration, this method sets up a listener function that
       * processes error events. The listener function calls the `handleEvent` method to trigger the associated event
       * handler.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      addOnErrorListener() {
        const eventDef = this.definition.onError;
        if (eventDef) {
          const listener = (event) => {
            this.handleEvent(eventDef, event);
          };
          this.webSocket.addEventListener('error', listener);
          this.registeredEventListeners.push({
            type: 'error',
            listener,
          });
        }
      }

      /**
       * Removes all event listeners previously added to the WebSocket instance.
       *
       * Iterates over the list of registered event listeners and removes each one from the WebSocket instance.
       * After removal, resets the list of registered event listeners.
       *
       * @private
       * @memberof VbWebSocket
       * @instance
       */
      removeEventListeners() {
        this.registeredEventListeners.forEach(({
          type,
          listener,
        }) => {
          this.webSocket.removeEventListener(type, listener);
        });
        this.registeredEventListeners = [];
      }

      /**
       * Handles an event based on the provided event definition.
       *
       * Creates a clone of the available contexts, adds the current event to it, generates listener chain functions
       * based on the event definition, and then calls these functions in parallel using the cloned context.
       *
       * @param {Object} eventDef - The definition of the event being handled.
       * @param {Object} originalEvent - The original native event object from the WebSocket
       * @param {string|Blob|Object} [message] - The type-converted message from the original event payload
       * @returns {*} The result of calling the listener chain functions in parallel.
       */
      handleEvent(eventDef, originalEvent, message) {
        const event = {
          originalEvent,
        };
        if (message !== undefined) {
          event.message = message;
        }

        const availableContext = this.container.getAvailableContexts().clone();
        availableContext[Constants.ContextName.EVENT] = event;
        availableContext[Constants.ContextName.WEB_SOCKET] = this.instance;

        const chainFunctionWrappers = this.container.createListenerChainFunctions(eventDef);

        // callListenerChainFunctionsInParallel is the original behavior, that calls chains in parallel
        return this.container.callListenerChainFunctionsInParallel(chainFunctionWrappers, availableContext);
      }

      /**
       * Disposes of the WebSocket connection, removing all event listeners and closing the socket.
       *
       * This method ensures that no further actions are triggered by the WebSocket connection.
       * It first removes all registered event listeners to prevent any action chains from being invoked.
       * Then, it closes the WebSocket connection and sets its state to 'disposed'.
       */
      dispose() {
        if (this.webSocket) {
          // first remove all registered listeners
          // remove all listeners to prevent any action chain from getting invoked
          this.removeEventListeners();

          this.state = 'disposed';

          try {
            this.webSocket.close();
          } catch (error) {
            logger.error('Failed to close WebSocket', this.name, error);
          }

          this.webSocket = null;
        }
      }
    }

    return VbWebSocket;
  });

